import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { accountingApi } from './api'
import { money, monthLabel } from './format'

const num = (n: number | null) => (n === null ? '—' : n.toLocaleString('en-US'))

/** SimpleCirc's own monthly figures, from its captured reports: read-only history
 * for the months before AAMCirc held each title's books. */
export function HistoryPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['accounting', 'history'], queryFn: accountingApi.history })
  const [slug, setSlug] = useState<string | null>(null)
  if (isLoading) return <p>Loading…</p>
  if (error || !data) return <p role="alert">Could not load the SimpleCirc history.</p>
  const title = data.titles.find((t) => t.slug === slug) ?? data.titles[0]
  if (!title) return <p className="text-paper/70">No SimpleCirc history has been captured.</p>
  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-serif text-2xl">SimpleCirc history</h1>
      <p className="max-w-2xl text-paper/70">SimpleCirc's own month-end figures, read from its Earned/Deferred report (for the month's issue) and its Cash Report. Earned revenue and the deferred balance only change in months with an issue. These months are read-only: they were closed in SimpleCirc.</p>
      <div role="tablist" aria-label="Title" className="flex flex-wrap gap-2">
        {data.titles.map((t) => (
          <button key={t.slug} role="tab" aria-selected={t.slug === title.slug} onClick={() => setSlug(t.slug)}
            className={`rounded-lg px-3 py-2 font-semibold ${t.slug === title.slug ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}>
            {t.name}
          </button>
        ))}
      </div>
      <section className="rounded-xl border border-ink-500 bg-ink-800 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[12px] text-paper/60">
            {title.takeover ? <>Up to {monthLabel(title.takeover)}, when AAMCirc took over the books.</> : <>AAMCirc does not hold this title's books yet.</>}
          </p>
          <a className="underline" href={accountingApi.historyCsvUrl(title.slug)}>Download CSV</a>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[900px] text-right text-[13px]">
            <thead className="text-[12px] text-paper/60">
              <tr>
                <th className="py-1 text-left">Month</th><th className="text-left">Issue</th>
                <th>Gross earned</th><th>Net earned</th><th>Agency commission</th>
                <th>Gross deferred</th><th>Net deferred</th><th>Copies deferred</th>
                <th>Cash</th><th>Payments</th>
              </tr>
            </thead>
            <tbody>
              {title.months.map((m) => (
                <tr key={m.month} className="border-t border-ink-600">
                  <td className="py-1 text-left">{monthLabel(m.month)}</td>
                  <td className="text-left text-paper/70">{m.issue ?? 'no issue'}</td>
                  <td>{money(m.gross_earned)}</td><td>{money(m.net_earned)}</td><td>{money(m.commission_earned)}</td>
                  <td>{money(m.gross_deferred)}</td><td>{money(m.net_deferred)}</td><td>{num(m.copies_deferred)}</td>
                  <td>{money(m.cash_total)}</td>
                  <td>{m.cash_payments === null ? '—' : <a className="underline" href={accountingApi.historyCashUrl(title.slug, m.month)}>{num(m.cash_payments)}</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
